import { MultiValue, SingleValue } from "chakra-react-select";
import emojiRegex from "emoji-regex";
import { TenorImage } from "gif-picker-react";
import {
  DistributionGroupFragment,
  GifInput,
  InternalAdminUserFragment,
  MessageFileInput,
  MessageFragment,
  MessageSearchFragment,
  MessageThreadType,
  MessageType,
  OutOfOfficeFragment,
  ReactionType,
  RemovedUserFragment,
  SearchFilter,
  SearchFilterType,
  SearchHighlightFragment,
  TaskStatusType,
  UserFragment,
  UserRoleType,
} from "gql/graphql";
import { lookup } from "mime-types";
import moment from "moment";
import { FieldError, FieldErrorsImpl, Merge } from "react-hook-form";
import FileResizer from "react-image-file-resizer";
import {
  CALLOUT_KEY,
  COMPRESSED_IMAGE_FORMAT,
  DATE_RANGE_TYPE,
  DISTRIBUTION_GROUP_REGEX,
  HASH_KEY,
  HEIC_CONVERSION_IMAGE_FORMAT,
  IMAGE_COMPRESSION_QUALITY,
  MAX_IMAGE_SIZE,
  TAG_REGEX,
  UserPreferenceName,
  USER_REGEX,
  FILE_COMPRESSION_BYTE_COUNT_THRESHOLD,
  HIGHLIGHT_PATHS,
  REACTION_TYPES,
  ACCESS_TOKEN,
  OOO_TIME_FORMAT,
  TaskDueDateOption,
  INVITE_PATH,
  SKIP_AUTH_PATHS,
  LOGOUT_PATH,
} from "./Constants";
import parsePhoneNumber from "libphonenumber-js";
import ObjectID from "bson-objectid";
import { IFileInTransit } from "modules/providers/index-db-provider/index-db-models";
import fetchFactory from "fetch-retry";
import { Cookies } from "react-cookie";
import { jwtDecode } from "jwt-decode";
import colors from "app/colors";
import { OptionGroup, FullOperator } from "react-querybuilder";
import { INVITE_ID_QUERY_STRING } from "modules/hooks/use-query-string";
import { FetchResult, NextLink, Observable, Operation } from "@apollo/client";

export const generateId = () =>
  Date.now().toString(32) + Math.random().toString(16);

// Escape regex metacharacters so arbitrary user input can be used safely inside
// `new RegExp(...)`. Without this, searches containing characters like ( [ \ * ?
// throw a SyntaxError and crash the page.
export const escapeRegExp = (value: string): string =>
  value.replace(/[|\\{}()[\]^$+*?.-]/g, (char) => `\\${char}`);

const eRegex = emojiRegex();

export const getEmojiCount = (value: string) => {
  let count = 0;
  while (eRegex.exec(value) !== null) {
    ++count;
  }
  return count;
};

export const isOnlyEmojis = (value: string) => {
  if (!value) return false;
  const noEmojiValue = value.replaceAll(eRegex, "");
  return noEmojiValue.trim().length === 0;
};

export const delay = (ms: number) =>
  // eslint-disable-next-line no-promise-executor-return
  new Promise((resolve) => setTimeout(resolve, ms));

export const getCleanEmail = (email: string) => {
  if (!email) return email;
  return email.toLowerCase().trim();
};

export const getUserFullName = (
  user?: { firstName: string; lastName: string } | null,
) => getFullName(user?.firstName, user?.lastName);

export const getUserFirstNameLastInitial = (
  user?: UserFragment | InternalAdminUserFragment | null,
) => `${user?.firstName ?? ""} ${(user?.lastName ?? "").slice(0, 1)}.`.trim();

export const getUserPreferenceValue = (
  name: UserPreferenceName,
  user?: UserFragment | InternalAdminUserFragment | null,
) =>
  user?.userPreferenceList?.find(
    (userPreference) => userPreference.name === name,
  )?.value || "";

export const getFullName = (firstName?: string, lastName?: string) =>
  `${firstName ?? ""} ${lastName ?? ""}`.trim();

export const sortTags = (
  a?: { displayName: string },
  b?: { displayName: string },
) => {
  if (!a || !b) {
    return 0;
  }
  return a.displayName.localeCompare(b.displayName, undefined, {
    numeric: true,
  });
};

export const sortDistributionGroups = (
  a: DistributionGroupFragment,
  b: DistributionGroupFragment,
) =>
  a.name.localeCompare(b.name, undefined, {
    numeric: true,
  });

export const sortUsersByFullName = (
  a?: { firstName: string; lastName: string } | null,
  b?: { firstName: string; lastName: string } | null,
) => getUserFullName(a).localeCompare(getUserFullName(b));

export const sortUsersByCallout = (a?: UserFragment, b?: UserFragment) =>
  (a?.callout || "").localeCompare(b?.callout || "");

export const sortByCallout = (
  a?: { callout?: string | null },
  b?: { callout?: string | null },
) => (a?.callout || "").localeCompare(b?.callout || "");

export const sortUsersByLastName = (a?: UserFragment, b?: UserFragment) =>
  (a?.lastName || "").localeCompare(b?.lastName || "");

export const sortUsersByEmailAddress = (a?: UserFragment, b?: UserFragment) =>
  (a?.email || "").localeCompare(b?.email || "");

export const sortById = (a?: { id: string }, b?: { id: string }) =>
  (a?.id || "").localeCompare(b?.id || "");

export const sortByCreateTime = (
  a?: { createTime: any },
  b?: { createTime: any },
) => moment(a?.createTime).diff(moment(b?.createTime));

export const sortByCreateTimeDesc = (
  a?: { createTime: any },
  b?: { createTime: any },
) => moment(b?.createTime).diff(moment(a?.createTime));

export const sortByName = (
  a?: { name?: string | null },
  b?: { name?: string | null },
) => (a?.name || "").localeCompare(b?.name || "");

export const getOrganizationHubspotUrl = (hubspotId: string) =>
  `https://app.hubspot.com/contacts/22212317/company/${hubspotId}/`;

export function isMultiValue<T>(
  arg: MultiValue<T> | SingleValue<T>,
): arg is MultiValue<T> {
  return Array.isArray(arg);
}

export function stopPropagate(callback: () => void) {
  return (e: { stopPropagation: () => void; preventDefault: () => void }) => {
    e.stopPropagation();
    e.preventDefault();
    callback();
  };
}

export const getDistributionGroupMention = (distributionGroup: {
  name: string;
}) => `${CALLOUT_KEY}${distributionGroup.name}`;

export const getUserMention = (user?: {
  callout?: string | null;
  email?: string | null;
  roleType: UserRoleType;
}) => {
  if (!user) return "";
  if (user.roleType === UserRoleType.Email) return user.email || "";
  return user.callout !== null ? `${CALLOUT_KEY}${user.callout}` : "";
};

export const getTagMention = (tag: { displayName: string }) =>
  `${HASH_KEY}${tag.displayName}`;

export const getTagIdListFromPlainText = (text?: string | null) => {
  if (!text) return [];
  const matchList = [...text.matchAll(TAG_REGEX)];
  return matchList.map((obj) => obj[1]);
};

export const getUserIdListFromPlainText = (text?: string | null) => {
  if (!text) return [];
  const matchList = [...text.matchAll(USER_REGEX)];
  return matchList.map((obj) => obj[1]);
};

export const getDistributionGroupIdListFromPlainText = (
  text?: string | null,
) => {
  if (!text) return [];
  const matchList = [...text.matchAll(DISTRIBUTION_GROUP_REGEX)];
  return matchList.map((obj) => obj[1]);
};

export const getMentionIdsFromText = (text?: string | null) => {
  if (!text) return [];
  return [
    ...getUserIdListFromPlainText(text),
    ...getTagIdListFromPlainText(text),
    ...getDistributionGroupIdListFromPlainText(text),
  ];
};

export const getGifInput = (tenorImage: TenorImage): GifInput => ({
  id: new ObjectID().toHexString(),
  url: tenorImage.url,
  name: tenorImage.description,
  imageUrl: tenorImage.url,
  videoUrl: tenorImage.url,
  height: tenorImage.height,
  width: tenorImage.width,
});

export const isImage = (contentType: string) =>
  contentType.includes("image/") &&
  contentType !== "image/vnd.adobe.photoshop" &&
  contentType !== "image/tiff";

export const isVideo = (contentType: string) =>
  contentType.startsWith("video/");

export const isAudio = (contentType: string) =>
  contentType.startsWith("audio/");

export const isText = (contentType: string) => contentType.startsWith("text/");

export const isPdf = (contentType: string) => contentType === "application/pdf";

export const isOffice = (contentType: string) =>
  contentType === "application/vnd.ms-excel" ||
  contentType === "application/msword" ||
  contentType === "application/vnd.ms-powerpoint" ||
  contentType ===
    "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" ||
  contentType ===
    "application/vnd.openxmlformats-officedocument.wordprocessingml.document" ||
  contentType ===
    "application/vnd.openxmlformats-officedocument.presentationml.presentation";

export const isCompressibleImage = (contentType: string, byteCount: number) =>
  byteCount > FILE_COMPRESSION_BYTE_COUNT_THRESHOLD &&
  ["image/jpeg", "image/png", "image/webp", "image/bmp"].includes(contentType);

export const isImageThumbnailable = (contentType: string) =>
  [
    "image/jpeg",
    "image/png",
    "image/svg+xml",
    "image/webp",
    "image/bmp",
    "image/gif",
    "image/vnd.microsoft.icon",
  ].includes(contentType);

export const isVideoThumbnailable = (contentType: string) =>
  contentType.startsWith("video/");

export const isHeic = (contentType: string) =>
  ["image/heic"].includes(contentType);

export const getMimeType = (file: File) => {
  if (file.type && file.type !== "") return file.type;
  const mimeType = lookup(file.name);
  if (mimeType) return mimeType;
  return "";
};

export const getBaseName = (file: File) => file.name.split(".").shift();

export const compressImage = (file: File) =>
  new Promise<File>((resolve, reject) => {
    FileResizer.imageFileResizer(
      file,
      MAX_IMAGE_SIZE,
      MAX_IMAGE_SIZE,
      COMPRESSED_IMAGE_FORMAT,
      IMAGE_COMPRESSION_QUALITY,
      0,
      (_file) => {
        if (_file instanceof File) {
          resolve(_file);
        } else {
          reject(new Error("Error compressing image"));
        }
      },
      "file",
    );
  });

export const convertHeicToJpeg = async (file: File) => {
  //https://github.com/alexcorvi/heic2any/issues/28
  const heic2any = (await import("heic2any")).default;
  const baseName = getBaseName(file);
  const blob = await heic2any({
    blob: file,
    toType: HEIC_CONVERSION_IMAGE_FORMAT,
  });
  const typeSafeBlob = blob instanceof Array ? blob : [blob];
  return new File(typeSafeBlob, `${baseName}.jpeg`);
};

export const getAdminExportFileName = (type: string) =>
  `Synco Admin ${type} Report - ${moment().format("YYYY-MM-DD")}.csv`;

export const forceToArray = <T>(value: T | T[]): T[] =>
  value instanceof Array ? value : [value];

export const getMessageFromError = (
  message?:
    | string
    | FieldError
    | Merge<FieldError, FieldErrorsImpl<any>>
    | undefined,
) => (typeof message === "string" ? message : "");

export const isMessageThreadPinned = (
  me: UserFragment | undefined,
  messageThreadId: string,
) => me?.pinnedMessageThreadIdList.includes(messageThreadId) ?? false;

export const getMessageThreadTypeName = ({
  type,
}: {
  type: MessageThreadType;
}) => {
  switch (type) {
    case MessageThreadType.Thread:
      return "Thread";
    case MessageThreadType.Chat:
      return "Chat";
    case MessageThreadType.Task:
      return "Task";
    default:
      return "N/A";
  }
};

export const getMixpanelActiveQuickFilters = (
  searchFilters: SearchFilter[],
) => {
  const hasToday = searchFilters.some(
    (filter) =>
      filter.type === SearchFilterType.DateRangeType &&
      filter.stringValue === DATE_RANGE_TYPE.LAST_24_HOURS,
  );
  const hasThisWeek = searchFilters.some(
    (filter) =>
      filter.type === SearchFilterType.DateRangeType &&
      filter.stringValue === DATE_RANGE_TYPE.LAST_7_DAYS,
  );
  const hasUnread = searchFilters.some(
    (filter) => filter.type === SearchFilterType.IsNotSeenByMe,
  );

  const activeQuickFilters: string[] = [];
  if (hasToday) activeQuickFilters.push("Today");
  if (hasThisWeek) activeQuickFilters.push("This Week");
  if (hasUnread) activeQuickFilters.push("Unread");
  return activeQuickFilters.join(",");
};

export const getMessageThreadSeenSubjectFontWeight = (isSeen?: boolean) =>
  isSeen ? 400 : 700;

export const getMessageThreadSeenFontWeight = (isSeen?: boolean) =>
  isSeen ? 500 : 600;

export const formatPhoneNumber = (phoneNumber?: string | null) => {
  if (!phoneNumber) return;
  const parsedPhoneNumber = parsePhoneNumber(phoneNumber);
  return parsedPhoneNumber ? parsedPhoneNumber.formatNational() : phoneNumber;
};

export const getE164PhoneNumber = (phoneNumber?: string | null) => {
  if (!phoneNumber) return;
  const parsedPhoneNumber = parsePhoneNumber(phoneNumber, "US");
  return parsedPhoneNumber ? parsedPhoneNumber.number : phoneNumber;
};

export const isSameMessage = (
  message1: { id: string },
  message2: { id: string },
) => message1.id === message2.id;

export const isUserActiveAndSmsOnly = (user?: UserFragment) => {
  if (!user) return false;
  const isSms = user.userPreferenceList?.find(
    (userPreference) => userPreference.name === "SMS",
  )?.value;
  return user.active === true && isSms === "true";
};

export const isUserSmsOnlyAndNotLoggedIn = (user?: UserFragment) => {
  if (!user) return false;
  return !user.firstLoginTime && isUserActiveAndSmsOnly(user);
};

export const isUserInactive = (user?: UserFragment) => {
  if (!user) return true;
  return !isUserActiveAndSmsOnly(user) && !user.firstLoginTime;
};

export const removedUsersForGroup = (
  distributionGroup?: DistributionGroupFragment,
  removedUserList?: RemovedUserFragment[],
) => {
  if (!distributionGroup) return [];
  const distributionGroupUsers = [
    ...distributionGroup.userIdList,
    ...distributionGroup.emailUserIdList,
  ];
  const removedUsers = (removedUserList ?? []).filter((removedUser) =>
    distributionGroupUsers.includes(removedUser.userId),
  );
  return removedUsers;
};

export const getHighlightWithTopScore = (
  highlights?: Array<SearchHighlightFragment> | null,
  path?: HIGHLIGHT_PATHS,
) => {
  if (!highlights) return undefined;
  const _highlights = path
    ? highlights.filter((highlight) => highlight.path === path)
    : highlights;
  if (_highlights.length === 0) return undefined;
  return _highlights.reduce((prev, current) =>
    prev.score && current.score && prev.score > current.score ? prev : current,
  );
};

export const getFileExtension = (fileName?: string | null) =>
  fileName ? fileName.split(".").pop() : undefined;

export const getFileKey = (organizationId: string, extension?: string) =>
  `${organizationId}/FILES/${new ObjectID().toHexString()}${extension ? `.${extension}` : ""}`;

export const sortReactionByType = (
  a: { type: ReactionType },
  b: { type: ReactionType },
): number => {
  const aIndex = REACTION_TYPES.indexOf(a.type);
  const bIndex = REACTION_TYPES.indexOf(b.type);
  if (aIndex === -1 || bIndex === -1) {
    return 0;
  }
  return aIndex - bIndex;
};

export const isUserInteractive = (user?: UserFragment) =>
  user && [UserRoleType.User, UserRoleType.Admin].includes(user.roleType);

export const getMessageFileInput = (
  file: IFileInTransit,
): MessageFileInput => ({
  id: file.id,
  name: file.name,
  contentType: file.contentType,
  isCompressed: !!file.isCompressed,
  isVoiceNote: file.isVoiceNote,
  key: file.key,
  previewKey: file.previewKey,
});

export const extractWords = (input?: string | null) => {
  if (!input) return [];
  return [
    ...new Set(
      input
        .split(/\s+/)
        .map((word) => word.toLowerCase())
        .filter((word) => word.length > 0),
    ),
  ];
};

export const getSearchHighlightHitWords = (
  messageSearches: MessageSearchFragment[],
): string[] => [
  ...new Set(
    messageSearches.flatMap((messageSearch) => {
      if (!messageSearch.highlights) return [];
      return messageSearch.highlights.flatMap((highlight) =>
        highlight.texts
          .filter((text) => text.type === "hit")
          .flatMap((text) => extractWords(text.value)),
      );
    }),
  ),
];

export const isOutOfOffice = (
  outOfOffice: OutOfOfficeFragment | undefined | null,
): boolean =>
  !!outOfOffice?.startTime &&
  moment().isBetween(
    moment(outOfOffice.startTime),
    outOfOffice?.endTime ? moment(outOfOffice?.endTime) : moment(),
    undefined,
    "[]",
  );

export const isUserOutOfOffice = ({ outOfOffice }: UserFragment): boolean =>
  isOutOfOffice(outOfOffice);

export type OutOfOfficeDisplayType = "single-day" | "no-last-day" | "default";

export const getOutOfOfficeDisplayType = (
  outOfOffice?: OutOfOfficeFragment | null,
): OutOfOfficeDisplayType | null => {
  if (!outOfOffice) {
    return null;
  }
  const isSingleDay = moment(outOfOffice.startTime).isSame(
    outOfOffice.endTime,
    "day",
  );
  const noLastDay = !outOfOffice.endTime;
  if (isSingleDay) return "single-day";
  if (noLastDay) return "no-last-day";
  return "default";
};

export const getUserOutOfOfficeNote = (
  outOfOffice?: OutOfOfficeFragment | null,
) => {
  if (!isOutOfOffice(outOfOffice)) return null;
  const displayType = getOutOfOfficeDisplayType(outOfOffice);
  if (!displayType || !outOfOffice) return null;
  if (displayType === "single-day") {
    return `Away on ${moment(outOfOffice.startTime).format(OOO_TIME_FORMAT)}`;
  }
  if (displayType === "no-last-day") {
    return `Away beginning ${moment(outOfOffice.startTime).format(OOO_TIME_FORMAT)}`;
  }
  return `Away ${moment(outOfOffice.startTime).format(OOO_TIME_FORMAT)} - ${moment(
    outOfOffice.endTime,
  ).format(OOO_TIME_FORMAT)}`;
};

const fetchRetry = fetchFactory(fetch);

const REFRESH_TOKEN_PATH = "/auth/refresh-token";

export const getAccessTokenCookie = () => {
  const cookies = new Cookies();
  return cookies.get(ACCESS_TOKEN, { doNotParse: true });
};

export const hasToken = () => {
  const accessTokenCookie = getAccessTokenCookie();
  return !!accessTokenCookie;
};

let fetchTokensPromise: Promise<string> | null = null;

export const fetchTokens = (): Promise<string> => {
  if (fetchTokensPromise) {
    return fetchTokensPromise;
  }

  fetchTokensPromise = fetchRetry(
    `${process.env.NEXT_PUBLIC_API_BASE_URL}${REFRESH_TOKEN_PATH}`,
    {
      method: "GET",
      credentials: "include",
      retries: 2,
      retryDelay: (attempt) => {
        const base = Math.min(2 ** attempt * 1000, 30000); // cap at 30s
        return base / 2 + Math.random() * (base / 2); // full jitter
      },
      retryOn: (attempt, _, response) => {
        if (!response) return true; // network error, retry
        const status = response.status;
        return status >= 500 || status === 429 || status === 408;
      },
    },
  )
    .then((res) => {
      if (!res.ok) throw new Error("Token refresh failed");

      const token = getAccessTokenCookie();
      if (!token) throw new Error("Access token missing after refresh");

      return token;
    })
    .finally(() => {
      fetchTokensPromise = null;
    });

  return fetchTokensPromise;
};

const TOKEN_EXPIRY_BUFFER_SECONDS = 30;

export const isTokenExpired = (token?: string) => {
  if (!token) return null;
  const { exp } = jwtDecode<{ exp: number }>(token);
  if (!exp) return false;
  const currentTimeInSeconds = Math.floor(Date.now() / 1000);
  return exp <= currentTimeInSeconds + TOKEN_EXPIRY_BUFFER_SECONDS;
};

export const isAccessTokenExpired = () => {
  if (!hasToken()) return true;
  return isTokenExpired(getAccessTokenCookie());
};

export const redirectToLogoutIfNeeded = () => {
  if (typeof window === "undefined") return;
  if (!SKIP_AUTH_PATHS.includes(window.location.pathname)) {
    window.location.href = LOGOUT_PATH;
  }
};

export const refreshAndForward = (
  operation: Operation,
  forward: NextLink,
): Observable<FetchResult> => {
  if (typeof window === "undefined") {
    return forward(operation); //SSR
  }
  return new Observable((observer) => {
    let cancelled = false;
    let innerSub: { unsubscribe: () => void } | null = null;

    fetchTokens()
      .then(() => {
        if (cancelled) return;
        if (!hasToken()) {
          redirectToLogoutIfNeeded();
          observer.complete();
          return;
        }
        innerSub = forward(operation).subscribe(observer);
      })
      .catch(() => {
        if (cancelled) return;
        redirectToLogoutIfNeeded();
        observer.complete();
      });

    return () => {
      cancelled = true;
      innerSub?.unsubscribe();
    };
  });
};

export const getJwtTokenSub = (token?: string) => {
  if (!token) return null;
  if (isTokenExpired(token)) return null;
  try {
    const decoded = jwtDecode<{ sub?: string }>(token);
    return decoded.sub ?? null;
  } catch {
    return null;
  }
};

export const isMessageThreadReadOnly = (messageThread: {
  createMessageUserIdList?: string[];
}) => (messageThread?.createMessageUserIdList ?? []).length > 0;

export const isMessageThreadReadOnlyAndIsReader = (
  messageThread: {
    createMessageUserIdList?: string[];
  },
  user: { id: string },
) =>
  isMessageThreadReadOnly(messageThread) &&
  !messageThread.createMessageUserIdList?.includes(user.id);

export const canCreateMessage = (
  messageThread: {
    createMessageUserIdList?: string[];
  },
  user?: { id: string },
) => {
  if (!isMessageThreadReadOnly(messageThread)) return true;
  return user && messageThread.createMessageUserIdList?.includes(user.id);
};

export const getMessageThreadSubjectColor = (messageThread: {
  isSeen?: boolean;
}) => (messageThread.isSeen ? colors.gray50 : colors.blackSecondary);

export const canForwardMessage = (message: MessageFragment) =>
  !message.deleted && message.type !== MessageType.System;

export const isOptionGroup = (
  option: FullOperator | OptionGroup<FullOperator>,
): option is OptionGroup<FullOperator> =>
  typeof option === "object" &&
  option !== null &&
  "options" in option &&
  Array.isArray((option as OptionGroup<FullOperator>).options);

export const findAllDistributionGroupAncestorsWithHistoricalAccess = (
  distributionGroups: readonly DistributionGroupFragment[],
  targetGroupId: string,
  visited = new Set<string>(),
) => {
  const result: DistributionGroupFragment[] = [];

  for (const group of distributionGroups) {
    if (
      group.distributionGroupIdList.includes(targetGroupId) &&
      !visited.has(group.id)
    ) {
      visited.add(group.id);
      if (group.isHistoricalAccess) result.push(group);

      // Recurse upward: find parents of this group as well
      result.push(
        ...findAllDistributionGroupAncestorsWithHistoricalAccess(
          distributionGroups,
          group.id,
          visited,
        ),
      );
    }
  }

  return result;
};

export const isValidPhoneNumberWithAreaCode = (phone: string): boolean => {
  const phoneNumber = parsePhoneNumber(phone, "US");
  if (!phoneNumber) return false;
  return phoneNumber?.isValid() && phoneNumber.nationalNumber.length === 10;
};

export const getObjectIds = (input?: string | null): string[] => {
  if (!input) return [];
  const objectIdRegex = /\b[a-f0-9]{24}\b/g;
  const matches = input.match(objectIdRegex);
  return matches ?? [];
};

export const sortUserAttributeCategories = (organization?: {
  userAttributeCategoryIdList?: string[] | null;
}) => {
  const orderMap = new Map(
    organization?.userAttributeCategoryIdList?.map((id, index) => [
      id,
      index,
    ]) ?? [],
  );

  return (a: { id: string }, b: { id: string }) => {
    const aIndex = orderMap.get(a.id) ?? Infinity;
    const bIndex = orderMap.get(b.id) ?? Infinity;
    return aIndex - bIndex;
  };
};

export const setToFivePM = (m: moment.Moment) =>
  m.hour(17).minute(0).second(0).millisecond(0);

export const isMessageEmpty = (message?: MessageFragment | null) => {
  if (!message) return true;
  const hasNoText = !message.text || message.text.trim().length === 0;
  const hasNoQuotedMessage =
    !message.quotedMessageList || message.quotedMessageList.length === 0;
  const hasNoFiles = message.fileList.length === 0;
  const hasNoGifs = message.gifList.length === 0;
  return hasNoText && hasNoQuotedMessage && hasNoFiles && hasNoGifs;
};

export const OPEN_TASK_STATUS_TYPES = [
  TaskStatusType.OnHold,
  TaskStatusType.InProgress,
  TaskStatusType.NotStarted,
];

export const CLOSED_TASK_STATUS_TYPES = [
  TaskStatusType.Complete,
  TaskStatusType.Abandoned,
];

export const getTaskStatusDescription = (
  statusType?: TaskStatusType | null,
) => {
  switch (statusType) {
    case TaskStatusType.Abandoned:
      return "Abandoned";
    case TaskStatusType.InProgress:
      return "In Progress";
    case TaskStatusType.NotStarted:
      return "Not Started";
    case TaskStatusType.OnHold:
      return "On Hold";
    case TaskStatusType.Complete:
      return "Completed";
    default:
      return "In Progress";
  }
};

export const getTaskDueDate = (
  dueDateOption?: TaskDueDateOption,
  dueTime?: string | null,
) => {
  if (!dueDateOption) return null;
  switch (dueDateOption) {
    case "Today":
      return setToFivePM(moment()).toISOString();
    case "Tomorrow":
      return setToFivePM(moment().add(1, "day")).toISOString();
    case "1 Week":
      return setToFivePM(moment().add(1, "week")).toISOString();
    case "Custom":
      return dueTime ? setToFivePM(moment(dueTime)).toISOString() : null;
    default:
      return null;
  }
};

export const getSelectedMessageThreadTypeBorderColor = (
  type?: MessageThreadType,
) => {
  switch (type) {
    case MessageThreadType.Chat: {
      return colors.blue;
    }
    case MessageThreadType.Task: {
      return colors.greenPrimary;
    }
    case MessageThreadType.Thread: {
      return colors.orangePrimary;
    }
    default:
      return undefined;
  }
};

export const toTitleCase = (str: string) =>
  str
    .toLowerCase()
    .split("_")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");

export const normalizeUrl = (url: string) => {
  try {
    return new URL(url).href;
  } catch {
    return new URL(`https://${url}`).href;
  }
};

export const getInviteLink = (baseUrl: string, inviteId: string) =>
  `${baseUrl}${INVITE_PATH}?${INVITE_ID_QUERY_STRING}=${inviteId}`;
